import React, { Fragment } from "react";
import { Link } from "react-location";
import { classNames } from "../../../utils";
import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setMobileMenuOpen } from "../../../features/settings";
import _ from "lodash";
import routes from "router/routes";
import { useTranslation } from "react-i18next";

type Props = {};

const NavLink = ({
    item,
    onLinkClick,
}: {
    item: (typeof routes)[number];
    onLinkClick?: () => void;
}) => {
    const { t } = useTranslation();
    const Icon = item.sidebar?.icon;

    return (
        <Link
            key={item.sidebar?.label}
            to={item?.path}
            getActiveProps={() => ({
                className:
                    "bg-green-800/70 text-white border-l-2 border-white",
            })}
            getInactiveProps={() => ({
                className:
                    "text-green-100 border-l-2 border-transparent hover:bg-green-800/50 hover:text-white hover:border-green-300",
            })}
            onClick={onLinkClick}
            className="group w-full px-4 py-2.5 flex items-center gap-3 text-sm font-medium transition-all duration-150">
            {({ isActive }) => (
                <>
                    <Icon
                        className={classNames(
                            isActive
                                ? "text-white"
                                : "text-green-300 group-hover:text-white",
                            "h-5 w-5 flex-shrink-0 transition-colors",
                        )}
                        aria-hidden="true"
                    />
                    <span className="truncate leading-tight">
                        {t(`${item?.sidebar?.label}`)}
                    </span>
                </>
            )}
        </Link>
    );
};

const NavItems = ({ onLinkClick }: { onLinkClick?: () => void }) => {
    const navigation = _.chain(routes)
        .filter((route) => route.meta?.layout === "App")
        .filter((item) => !!item.sidebar)
        .value();

    const mainNav = navigation.filter(
        (item) => item.meta?.section !== "Config",
    );
    const bottomNav = navigation.filter(
        (item) => item.meta?.section === "Config",
    );

    return (
        <>
            <div className="flex-1 overflow-y-auto py-2">
                <nav className="space-y-0.5 px-2">
                    {mainNav.map((item) => (
                        <NavLink
                            key={item.sidebar?.label}
                            item={item}
                            onLinkClick={onLinkClick}
                        />
                    ))}
                </nav>
            </div>

            {bottomNav.length > 0 && (
                <div className="border-t border-green-600 py-2">
                    <nav className="space-y-0.5 px-2">
                        {bottomNav.map((item) => (
                            <NavLink
                                key={item.sidebar?.label}
                                item={item}
                                onLinkClick={onLinkClick}
                            />
                        ))}
                    </nav>
                </div>
            )}
        </>
    );
};

const Sidebar = (props: Props) => {
    const { mobileMenuOpen } = useAppSelector((state) => state.settings);
    const dispatch = useAppDispatch();

    return (
        <Fragment>
            <div className="hidden md:flex md:flex-shrink-0">
                <div className="flex flex-col w-56 bg-green-700 h-screen">
                    <div className="flex items-center h-16 px-5 border-b border-green-600 flex-shrink-0">
                        <span className="text-white font-bold text-xl tracking-wide">Kava</span>
                    </div>

                    <div className="flex flex-col flex-1 overflow-hidden">
                        <NavItems />
                    </div>
                </div>
            </div>

            <Transition show={mobileMenuOpen} as={Fragment}>
                <Dialog
                    as="div"
                    static
                    className="fixed inset-0 z-40 flex md:hidden"
                    open={mobileMenuOpen}
                    onClose={(value: boolean) =>
                        dispatch(setMobileMenuOpen(value))
                    }>
                    <TransitionChild
                        as="div"
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        className="fixed inset-0 bg-gray-600 bg-opacity-75"
                    />
                    <TransitionChild
                        as="div"
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full">
                        <div className="relative flex flex-col w-full max-w-xs bg-green-700 h-full">
                            <TransitionChild
                                as="div"
                                enter="ease-in-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in-out duration-300"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <div className="absolute top-1 right-0 -mr-14 p-1">
                                    <button
                                        type="button"
                                        className="h-12 w-12 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white"
                                        onClick={() =>
                                            dispatch(setMobileMenuOpen(false))
                                        }>
                                        <XMarkIcon
                                            className="h-6 w-6 text-white"
                                            aria-hidden="true"
                                        />
                                        <span className="sr-only">
                                            Close sidebar
                                        </span>
                                    </button>
                                </div>
                            </TransitionChild>

                            <div className="flex items-center h-16 px-5 border-b border-green-600 flex-shrink-0">
                                <span className="text-white font-bold text-xl tracking-wide">Kava</span>
                            </div>

                            <div className="flex flex-col flex-1 overflow-hidden">
                                <NavItems
                                    onLinkClick={() =>
                                        dispatch(setMobileMenuOpen(false))
                                    }
                                />
                            </div>
                        </div>
                    </TransitionChild>
                    <div className="flex-shrink-0 w-14" aria-hidden="true" />
                </Dialog>
            </Transition>
        </Fragment>
    );
};

export default Sidebar;
